import { toast } from "sonner";
import { API_ENDPOINTS, HTTP_METHODS } from "../config/api";
import { BaseApiService, ApiResponse } from "./baseApi";

export interface HsnCode {
  id: number;
  hsnCode: string;
  description: string;
  chapter: string;
  heading: string;
  subHeading: string;
  currentIgstRate: number;
  currentCgstRate: number;
  currentSgstRate: number;
  currentCessRate: number;
  isActive: boolean;
  branchId: number;
  createdBy: number;
  updatedBy: number;
  createdAt: string;
  updatedAt: string;
}

export interface HsnCodeCreateRequest {
  hsnCode: string;
  description: string;
  chapter: string;
  heading: string;
  subHeading: string;
  currentIgstRate: number;
  currentCgstRate: number;
  currentSgstRate: number;
  currentCessRate: number;
  isActive: boolean;
  branchId: number;
  taxRateEffectiveFrom?: string;
  taxRateChangeReason?: string;
  taxRateChangedBy?: string;
}

export interface HsnCodeUpdateRequest extends HsnCodeCreateRequest {
  id: number;
}

class HsnApiService extends BaseApiService {
  // Get all HSN codes
  async getHsnCodes(): Promise<HsnCode[]> {
    try {
      const response = await this.makeRequest<ApiResponse<HsnCode[]>>(API_ENDPOINTS.HSN_CODES.LIST);
      if (response.success) {
        return response.data;
      }
      throw new Error(response.message || 'Failed to fetch HSN codes');
    } catch (error) {
      toast.error('Failed to fetch HSN codes');
      throw error;
    }
  }

  // Get HSN code by ID
  async getHsnCodeById(id: string): Promise<HsnCode> {
    try {
      const response = await this.makeRequest<ApiResponse<HsnCode>>(API_ENDPOINTS.HSN_CODES.DETAIL(id));
      if (response.success) {
        return response.data;
      }
      throw new Error(response.message || 'Failed to fetch HSN code details');
    } catch (error) {
      toast.error('Failed to fetch HSN code details');
      throw error;
    }
  }

  // Create new HSN code
  async createHsnCode(hsnCodeData: HsnCodeCreateRequest): Promise<HsnCode> {
    try {
      const response = await this.makeRequest<ApiResponse<HsnCode>>(API_ENDPOINTS.HSN_CODES.CREATE, {
        method: HTTP_METHODS.POST,
        body: JSON.stringify(hsnCodeData),
      });
      if (response.success) {
        toast.success(response.message || 'HSN code created successfully!');
        return response.data;
      }
      throw new Error(response.message || 'Failed to create HSN code');
    } catch (error) {
      toast.error('Failed to create HSN code');
      throw error;
    }
  }

  // Update HSN code
  async updateHsnCode(id: string, hsnCodeData: HsnCodeUpdateRequest): Promise<HsnCode> {
    try {
      const response = await this.makeRequest<ApiResponse<HsnCode>>(API_ENDPOINTS.HSN_CODES.UPDATE(id), {
        method: HTTP_METHODS.PUT,
        body: JSON.stringify(hsnCodeData),
      });
      // If response has data, consider it successful (even if success field is missing or false)
      // This handles cases where backend returns data but success field might be missing
      if (response.data) {
        toast.success(response.message || 'HSN code updated successfully!');
        return response.data;
      }
      // Only throw error if we explicitly have success: false and no data
      if (response.success === false) {
        throw new Error(response.message || 'Failed to update HSN code');
      }
      throw new Error(response.message || 'Failed to update HSN code');
    } catch (error) {
      // Only show error toast for actual errors (not if we already showed success)
      if (error instanceof Error && error.message.includes('Failed to update')) {
        toast.error('Failed to update HSN code');
      }
      throw error;
    }
  }

  // Delete HSN code
  async deleteHsnCode(id: string): Promise<void> {
    try {
      const response = await this.makeRequest<ApiResponse<unknown>>(API_ENDPOINTS.HSN_CODES.DELETE(id), {
        method: HTTP_METHODS.DELETE,
      });
      if (response?.success) {
        toast.success(response.message || 'HSN code deleted successfully!');
        return;
      }
      throw new Error(response?.message || 'Failed to delete HSN code');
    } catch (error) {
      toast.error('Failed to delete HSN code');
      throw error;
    }
  }

  // Search HSN codes
  async searchHsnCodes(query: string): Promise<HsnCode[]> {
    try {
      const params = new URLSearchParams({
        q: query,
      });
      const response = await this.makeRequest<ApiResponse<HsnCode[]>>(`${API_ENDPOINTS.HSN_CODES.LIST}?${params.toString()}`);
      if (response.success) {
        return response.data;
      }
      throw new Error(response.message || 'Failed to search HSN codes');
    } catch (error) {
      toast.error('Failed to search HSN codes');
      throw error;
    }
  }

  // Get HSN codes by branch
  async getHsnCodesByBranch(branchId: number): Promise<HsnCode[]> {
    try {
      const response = await this.makeRequest<ApiResponse<HsnCode[]>>(`${API_ENDPOINTS.HSN_CODES.LIST}?branchId=${branchId}`);
      if (response.success) {
        return response.data;
      }
      throw new Error(response.message || 'Failed to fetch HSN codes for branch');
    } catch (error) {
      toast.error('Failed to fetch HSN codes for branch');
      throw error;
    }
  }

  // Get active HSN codes only
  async getActiveHsnCodes(): Promise<HsnCode[]> {
    try {
      const response = await this.makeRequest<ApiResponse<HsnCode[]>>(`${API_ENDPOINTS.HSN_CODES.LIST}?isActive=true`);
      if (response.success) {
        return response.data;
      }
      throw new Error(response.message || 'Failed to fetch active HSN codes');
    } catch (error) {
      toast.error('Failed to fetch active HSN codes');
      throw error;
    }
  }
}

export const hsnApiService = new HsnApiService();
